'use client';

import AnsweringServiceVsGreet from '@/components/AnsweringServiceVsGreet';
import LandingLayout from '@/components/LandingLayout';
import { useAuth } from '@/contexts/AuthContextNext';
import { trackButtonClick, trackPageView } from '@/lib/analytics';
import {
  getPlanDisplayPrice,
  PRICING_PLANS,
  PUBLIC_SALE_STARTING_PRICE,
} from '@/types/pricing';
import { PhoneIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import { useEffect } from 'react';

export default function AiReceptionistPricingPage() {
  const { isAuthenticated, isInitialized } = useAuth();
  const pricingContext = { isAuthenticated: !isInitialized || isAuthenticated };

  useEffect(() => {
    trackPageView('AI Receptionist Pricing Landing', undefined, {
      funnel_step: 'query_match_landing',
    });
  }, []);

  return (
    <LandingLayout>
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <div className="mb-6 inline-flex items-center gap-3 rounded-full bg-blue-500/20 border border-blue-400/30 px-4 py-2 text-sm font-semibold text-blue-100">
            <span className="h-2 w-2 rounded-full bg-blue-400" />
            AI receptionist pricing
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            AI Receptionist Pricing: Flat Rate, No Per-Minute Surprises
          </h1>
          <p className="text-xl text-blue-100 mb-4 max-w-2xl">
            Traditional answering services often run $200–$400+/mo once overages hit. Greet starts at{' '}
            {PUBLIC_SALE_STARTING_PRICE} with 24/7 included.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <Link
              href="/try"
              onClick={() => trackButtonClick('try_demo', 'ai_pricing_hero')}
              className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
            >
              <PhoneIcon className="h-5 w-5 mr-2" />
              Hear it answer in 60 seconds
            </Link>
            <Link
              href="/pricing"
              onClick={() => trackButtonClick('view_pricing', 'ai_pricing_hero')}
              className="bg-white/10 border border-white text-white hover:bg-white/20 px-8 py-4 rounded-lg font-semibold text-lg transition-colors text-center"
            >
              See full plan details
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">Current plans</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {PRICING_PLANS.map((plan) => {
              const display = getPlanDisplayPrice(plan, pricingContext);
              const popular = plan.id === 'growth';
              return (
                <div
                  key={plan.id}
                  className={`rounded-2xl border p-8 ${popular ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'}`}
                >
                  <div className="text-sm font-semibold text-gray-500 mb-2">{plan.name}</div>
                  {display.showSale && (
                    <div className="text-lg text-gray-400 line-through">
                      {display.basePrice.replace('/month', '')}
                    </div>
                  )}
                  <div className="text-4xl font-bold text-gray-900">
                    {display.price.replace('/month', '')}
                    <span className="text-lg font-medium text-gray-500">/mo</span>
                  </div>
                  {popular && (
                    <div className="mt-3 inline-block text-xs font-semibold bg-blue-100 text-blue-800 px-3 py-1 rounded-full">
                      Most popular
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-center text-gray-600 mt-8">
            7-day free trial · Cancel anytime · No contracts
          </p>
        </div>
      </section>

      <AnsweringServiceVsGreet
        industryLabel="Small Businesses"
        analyticsContext="ai_pricing_comparison"
        accent="blue"
      />

      <section className="py-16 bg-gray-50 text-center px-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to compare live?</h2>
        <p className="text-gray-600 mb-6 max-w-xl mx-auto">
          Try the product before you buy-or jump straight to checkout on the full pricing page.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            href="/try"
            onClick={() => trackButtonClick('try_demo', 'ai_pricing_footer')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold"
          >
            Try demo
          </Link>
          <Link
            href="/pricing"
            onClick={() => trackButtonClick('view_pricing', 'ai_pricing_footer')}
            className="border border-gray-300 hover:bg-white px-8 py-4 rounded-lg font-semibold text-gray-800"
          >
            Full pricing page
          </Link>
        </div>
      </section>
    </LandingLayout>
  );
}
